import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Send } from 'lucide-react';
import { playSound, SOUNDS } from '../App';

const CLIPPY_RESPONSES = [
  "Det ser ut som du prøver å bygge AI. Vil du ha hjelp med det?",
  "Jeg ser du er interessert i autonome systemer! Teamet vårt spesialiserer seg på AI som faktisk fungerer i produksjon.",
  "Morsomt faktum: Vi har 99.7% oppetidsgaranti. Det er mye bedre enn fetteren min Karl som krasjer hele tiden.",
  "Trenger du hjelp med automatisering? Vi leverer på uker, ikke kvartaler!",
  "Jeg er en AI-assistent fra 1997, men teamet hos Autonomous bygger AI for 2025 og fremover!",
  "Tips: Dobbeltklikk på Kontakt for å starte en samtale med ekte mennesker!",
  "Visste du? Vi har hatt 0 feilede AI-piloter. Null. Nada. Ingenting.",
  "Ser du etter AI-strategi? Dropp 200-siders rapportene - vi leverer veikart som er i produksjon innen 90 dager.",
  "Jeg er kanskje bare en binders, men Autonomous-teamet bygger agenter som forsker, beslutter og utfører!",
  "Norsk presisjon møter AI-utvikling!"
];

export default function ClippyAI({ onDismiss, onOpenContact }) {
  const [messages, setMessages] = useState([{
    from: 'clippy',
    text: "👋 Hei! Jeg er Clippy, din AI-assistent!\n\nDet ser ut som du prøver å bygge AI. Vil du ha hjelp med det?"
  }]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getClippyResponse = (userMessage) => {
    const lower = userMessage.toLowerCase();
    
    if (lower.includes('kontakt') || lower.includes('snakke') || lower.includes('menneske')) {
      return "Flott idé! La meg åpne kontaktskjemaet for deg... 📧";
    }
    if (lower.includes('tjeneste') || lower.includes('hva gjør dere')) {
      return "Vi bygger AI-systemer som faktisk fungerer! Agentisk AI, strategirådgivning, prosessautomatisering, dataintelligens og ytelsesoptimalisering. Dobbeltklikk på Tjenester for å lære mer!";
    }
    if (lower.includes('hallo') || lower.includes('hei') || lower.includes('hey')) {
      return "Hei der! 👋 Jeg er Clippy, her for å hjelpe deg lære om Autonomous. Hva vil du vite?";
    }
    if (lower.includes('vits') || lower.includes('morsom')) {
      return "Hvorfor gikk AI-en til terapi? Fordi den hadde for mange dyplæringsproblemer! 😄";
    }
    if (lower.includes('norge') || lower.includes('norsk')) {
      return "Ja! Autonomous er basert i Norge. Vi bringer nordisk direkthet til AI: si hva vi skal gjøre, gjør det vi sier, og ikke kast bort noens tid. 🇳🇴";
    }
    if (lower.includes('oppetid') || lower.includes('pålitelig')) {
      return "99.7% oppetid eller vi fikser det gratis! Systemene våre fungerer klokken 3 om natten når ingen ser. Det er vårt løfte.";
    }
    if (lower.includes('pris') || lower.includes('kost') || lower.includes('hvor mye')) {
      return "Prising avhenger av prosjektomfanget. Beste måten å få et tilbud er å fylle ut kontaktskjemaet - teamet vårt svarer innen 24 timer!";
    }
    
    // Random response
    return CLIPPY_RESPONSES[Math.floor(Math.random() * CLIPPY_RESPONSES.length)];
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = input.trim();
    setMessages(prev => [...prev, { from: 'user', text: userMessage }]);
    setInput('');
    setIsTyping(true);
    playSound(SOUNDS.click);

    // Check if user wants to contact
    if (userMessage.toLowerCase().includes('kontakt') || userMessage.toLowerCase().includes('snakke')) {
      setTimeout(() => {
        setIsTyping(false);
        setMessages(prev => [...prev, { from: 'clippy', text: getClippyResponse(userMessage) }]);
        setTimeout(() => onOpenContact?.(), 1000);
      }, 1000);
      return;
    }

    // Simulate typing delay
    setTimeout(() => {
      setIsTyping(false);
      setMessages(prev => [...prev, { from: 'clippy', text: getClippyResponse(userMessage) }]);
    }, 1000 + Math.random() * 1000);
  };

  const handleContact = () => {
    playSound(SOUNDS.click);
    onOpenContact();
    onDismiss();
  };

  return (
    <motion.div
      className="clippy-ai-container"
      initial={{ opacity: 0, scale: 0.8, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.8, y: 20 }}
      transition={{ duration: 0.3, type: 'spring' }}
    >
      <div className="clippy-ai-window">
        <div className="clippy-ai-header">
          <span>Clippy AI-Assistent</span>
          <button className="clippy-ai-close" onClick={onDismiss}>
            <X size={12} />
          </button>
        </div>
        
        <div className="clippy-ai-messages">
          {messages.map((msg, i) => (
            <div key={i} className={`clippy-message ${msg.from}`}>
              {msg.from === 'clippy' && <span className="clippy-avatar">📎</span>}
              <div className="clippy-message-text">{msg.text}</div>
            </div>
          ))}
          {isTyping && (
            <div className="clippy-message clippy">
              <span className="clippy-avatar">📎</span>
              <div className="clippy-typing">
                <span></span><span></span><span></span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSubmit} className="clippy-ai-input">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Spør Clippy om hva som helst..."
          />
          <button type="submit"><Send size={14} /></button>
        </form>

        <div className="clippy-ai-actions">
          <button onClick={handleContact}>📧 Kontakt Oss</button>
          <button onClick={onDismiss}>Kanskje Senere</button>
        </div>
      </div>

      <div className="clippy-character-large">
        📎
      </div>
    </motion.div>
  );
}
